<template>
  <v-container fluid>
    <v-card class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-account-group</v-icon>
        <v-toolbar-title>Usuarios</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-col cols="3" class="mr-1" v-if="$vuetify.breakpoint.mdAndUp">
          <v-text-field v-model="search" label="Buscar usuario..." prepend-inner-icon="mdi-magnify"
            hide-details clearable rounded single-line filled dense dark></v-text-field>
        </v-col>
        <v-col cols="3" class="mr-3" v-if="$vuetify.breakpoint.mdAndUp">
          <v-select v-model="dealerFilter" :items="dealers" item-text="name" item-value="id" label="Filtrar por dealer"
            clearable hide-details rounded filled dense dark @change="load"></v-select>
        </v-col>
        <v-btn icon @click="importDialog = true" title="Volcado masivo"><v-icon>mdi-file-upload</v-icon></v-btn>
        <v-btn icon @click="openForm(null)" title="Nuevo usuario"><v-icon>mdi-plus</v-icon></v-btn>
        <v-btn icon @click="load" title="Actualizar"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-data-table :headers="headers" :items="users" :search="search" :custom-filter="filterUsers" :loading="loading" item-key="id"
        :items-per-page="10" :footer-props="{ itemsPerPageOptions: [10, 25, 50] }" no-data-text="No hay usuarios">
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
          <div class="text-caption grey--text">{{ item.email }}</div>
        </template>
        <template v-slot:item.role="{ item }">{{ userRoleLabel(item) }}</template>
        <template v-slot:item.conditions="{ item }">
          <v-chip v-for="c in item.conditions" :key="c" x-small class="mr-1" color="primary" outlined>{{ conditionLabel(c) }}</v-chip>
        </template>
        <template v-slot:item.dealer="{ item }">{{ item.dealer ? item.dealer.name : '—' }}</template>
        <template v-slot:item.permissions_customized="{ item }">
          <v-chip x-small :color="item.permissions_customized ? 'orange' : 'grey lighten-1'" dark>
            {{ item.permissions_customized ? 'Modificados por el dealer' : 'Predefinidos' }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="item.status === STATUS_ACTIVE ? 'success' : 'grey'" dark>
            {{ item.status === STATUS_ACTIVE ? 'Activo' : 'Inactivo' }}
          </v-chip>
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn icon small color="primary" @click="openForm(item)" title="Editar"><v-icon>mdi-pencil</v-icon></v-btn>
          <v-btn icon small :color="item.status === STATUS_ACTIVE ? 'error' : 'success'" @click="toggleStatus(item)"
            :title="item.status === STATUS_ACTIVE ? 'Desactivar' : 'Activar'">
            <v-icon>{{ item.status === STATUS_ACTIVE ? 'mdi-account-cancel' : 'mdi-account-check' }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="620px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-account</v-icon>
          {{ editing ? 'Editar usuario' : 'Nuevo usuario' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-row dense>
              <v-col cols="6"><v-text-field v-model="form.name" label="Nombre *" outlined dense :rules="[required]"></v-text-field></v-col>
              <v-col cols="6"><v-text-field v-model="form.lastname" label="Apellidos *" outlined dense :rules="[required]"></v-text-field></v-col>
            </v-row>
            <v-row dense>
              <v-col cols="6"><v-text-field v-model="form.email" label="Email *" outlined dense :rules="[required]"></v-text-field></v-col>
              <v-col cols="6"><v-text-field v-model="form.phone" label="Teléfono" outlined dense></v-text-field></v-col>
            </v-row>
            <v-row dense>
              <v-col cols="6">
                <v-select v-model="form.dealer_id" :items="dealers" item-text="name" item-value="id" label="Dealer *"
                  outlined dense :rules="[required]" @change="onDealerChange"></v-select>
              </v-col>
              <v-col cols="6">
                <v-select v-model="form.role" :items="DEALER_ROLE_OPTIONS" label="Rol *" outlined dense :rules="[required]"></v-select>
              </v-col>
            </v-row>
            <div class="text-subtitle-2 mb-1">Condiciones *</div>
            <div class="d-flex">
              <v-checkbox v-for="c in dealerConditions" :key="c.value" v-model="form.conditions" :value="c.value"
                :label="c.text" class="mr-6 mt-0" hide-details></v-checkbox>
              <span v-if="form.dealer_id && !dealerConditions.length" class="grey--text text-caption">El dealer no tiene condiciones activas</span>
            </div>
            <v-alert type="info" text dense class="mt-4 mb-0">
              {{ editing ? 'Al cambiar el rol se aplican los permisos predefinidos del nuevo rol; se mantienen los ajustes del dealer.' : 'Recibirá los permisos predefinidos de su rol y un email de acceso.' }}
            </v-alert>
            <user-partner-permissions v-if="editing" :user-id="editing.id" :key="editing.id" class="mt-4"></user-partner-permissions>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!valid || !form.conditions.length" :loading="saving" @click="save">
            <v-icon left>mdi-content-save</v-icon>
            {{ editing ? 'Guardar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <import-dialog v-model="importDialog" title="Volcado masivo de usuarios" template-name="plantilla_usuarios.xlsx"
      :template-fn="templateFn" :import-fn="importFn" @imported="load"></import-dialog>
  </v-container>
</template>

<script>
import GrupoPanelService from '@/services/GrupoPanelService'
import UserPartnerPermissions from '@/components/UserPartnerPermissions'
import ImportDialog from '@/components/grupo/ImportDialog'
import { DEALER_ROLES, DEALER_ROLE_OPTIONS, CONDITIONS, STATUS_ACTIVE, STATUS_INACTIVE, roleLabel, conditionLabel, apiData, apiError } from '@/grupo'

const emptyForm = () => ({ name: '', lastname: '', email: '', phone: '', dealer_id: null, role: null, conditions: [] })

export default {
  name: 'GrupoUsers',

  components: { ImportDialog, UserPartnerPermissions },

  data: () => ({
    DEALER_ROLES,
    DEALER_ROLE_OPTIONS,
    STATUS_ACTIVE,
    users: [],
    dealers: [],
    dealerFilter: null,
    search: '',
    loading: false,
    dialog: false,
    importDialog: false,
    editing: null,
    valid: false,
    saving: false,
    form: emptyForm(),
    headers: [
      { text: 'Usuario', value: 'name' },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Rol', value: 'role', sortable: false },
      { text: 'Condiciones', value: 'conditions', sortable: false },
      { text: 'Dealer', value: 'dealer', sortable: false },
      { text: 'Permisos', value: 'permissions_customized', sortable: false },
      { text: 'Estado', value: 'status', align: 'center' },
      { text: '', value: 'action', sortable: false, align: 'end' }
    ]
  }),

  computed: {
    dealerConditions () {
      const dealer = this.dealers.find(d => d.id === this.form.dealer_id)
      return dealer ? CONDITIONS.filter(c => dealer.conditions.includes(c.value)) : []
    }
  },

  mounted () {
    this.loadDealers()
    this.load()
  },

  methods: {
    // Busca por nombre, apellidos, email, telefono y dealer (el filtro por defecto solo mira la columna name)
    filterUsers (value, search, item) {
      const q = (search || '').toString().toLowerCase().trim()
      if (!q) return true
      return [item.name, item.lastname, `${item.name || ''} ${item.lastname || ''}`, item.email, item.phone, item.dealer && item.dealer.name]
        .some(v => v && v.toString().toLowerCase().includes(q))
    },
    conditionLabel,
    required: v => !!v || 'Obligatorio',
    templateFn: () => GrupoPanelService.userImportTemplate(),
    importFn: (file, dryRun) => GrupoPanelService.importUsers(file, dryRun),

    userRole (user) {
      return user.roles.find(r => DEALER_ROLES.some(d => d.value === r)) || null
    },

    userRoleLabel (user) {
      const role = this.userRole(user)
      return role ? roleLabel(role) : '—'
    },

    async loadDealers () {
      try {
        this.dealers = apiData(await GrupoPanelService.dealers()) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los concesionarios'))
      }
    },

    async load () {
      this.loading = true
      try {
        const params = this.dealerFilter ? { dealer_id: this.dealerFilter } : {}
        this.users = apiData(await GrupoPanelService.users(params)) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los usuarios'))
      } finally {
        this.loading = false
      }
    },

    onDealerChange () {
      // Solo condiciones activas en el dealer elegido
      const allowed = this.dealerConditions.map(c => c.value)
      this.form.conditions = this.form.conditions.filter(c => allowed.includes(c))
    },

    openForm (user) {
      this.editing = user
      this.form = user ? {
        name: user.name,
        lastname: user.lastname,
        email: user.email,
        phone: user.phone,
        dealer_id: user.dealer ? user.dealer.id : null,
        role: this.userRole(user) === 'garage' ? 'sales' : this.userRole(user),
        conditions: [...user.conditions]
      } : emptyForm()
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },

    async save () {
      this.saving = true
      try {
        if (this.editing) {
          await GrupoPanelService.updateUser(this.editing.id, this.form)
          this.$toast.success('Usuario actualizado')
        } else {
          await GrupoPanelService.createUser(this.form)
          this.$toast.success('Usuario creado. Recibirá un email de acceso.')
        }
        this.dialog = false
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo guardar el usuario'))
      } finally {
        this.saving = false
      }
    },

    async toggleStatus (user) {
      const activate = user.status !== STATUS_ACTIVE
      const ok = await this.$confirm(activate
        ? `¿Activar a ${user.name} ${user.lastname}?`
        : `¿Desactivar a ${user.name} ${user.lastname}? No podrá acceder, pero no se borra.`, { title: activate ? 'Activar' : 'Desactivar' })
      if (!ok) return
      try {
        await GrupoPanelService.setUserStatus(user.id, activate ? STATUS_ACTIVE : STATUS_INACTIVE)
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo cambiar el estado'))
      }
    }
  }
}
</script>
